import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import {
  getSupportTickets,
  queryKeys,
  SUPPORT_TICKETS_PAGE_SIZE,
  SupportTicketType,
} from '@vgrubs/components';
import { SupportTicketTab, tabFilters } from '../helpers/supportTicketLabels';

type Filters = {
  tab: SupportTicketTab;
  type: SupportTicketType | null;
  locationId: number | null;
  viewerId: number;
};

/**
 * A page of tickets at a time, in the order the server put them: open before
 * claimed before resolved, oldest first inside each. Nothing is re-sorted or
 * filtered here - see `tabFilters`.
 */
export const useSupportTicketsList = ({
  tab,
  type,
  locationId,
  viewerId,
}: Filters) => {
  const queryClient = useQueryClient();
  const { status, claimedById } = tabFilters(tab, viewerId);

  const query = useInfiniteQuery({
    queryKey: queryKeys.supportTickets.list(
      status,
      type,
      claimedById ?? null,
      locationId
    ),
    queryFn: async ({ pageParam }) => {
      const { data } = await getSupportTickets({
        page: pageParam,
        limit: SUPPORT_TICKETS_PAGE_SIZE,
        status,
        ...(claimedById ? { claimedById } : {}),
        ...(type ? { type } : {}),
        ...(locationId ? { locationId } : {}),
      });

      /* Every page carries the unfiltered open count, so looking at the list
         corrects the sidebar badge for free - and covers the socket event this
         tab was asleep for. */
      queryClient.setQueryData(queryKeys.supportTickets.openCount, {
        open: data.data.open,
      });

      return data.data;
    },
    initialPageParam: 1,
    // `total` is the count for these filters, so the end is arithmetic rather
    // than a short page - which this endpoint would not tell us about anyway.
    getNextPageParam: (lastPage, pages) =>
      pages.length * SUPPORT_TICKETS_PAGE_SIZE < lastPage.total
        ? pages.length + 1
        : undefined,
  });

  return {
    tickets: (query.data?.pages ?? []).flatMap((page) => page.items),
    total: query.data?.pages[0]?.total ?? 0,
    isLoading: query.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    error: query.error,
  };
};
